/* ---------------------------------------------------------------------------
   The values a rating standard supplies where no document about the structure
   does — one list, used by the cloud-model reader on the server and by the form
   when there is no model to read.

   EVERY VALUE HERE IS MARKED `standard`, never `model` or `drawing`, and every
   one carries the sentence saying where it came from. They fill BLANK fields
   only: a value somebody typed, or one the model stated, is never replaced.

   What is NOT here is the structure itself — a slab thickness, a bar area, a
   girder depth. Those differ bridge to bridge, and a default for them would be
   a rating of some other bridge.
   --------------------------------------------------------------------------- */

import { DPWH_CBPLR, type EraRow } from '../cbplr/profile.ts';

export type DefaultFamily = 'RCFS' | 'RCDG' | 'PCDG';

export interface StandardValue {
  key: string;
  value: number;
  /** One sentence naming the standard or convention it came from. */
  why: string;
}

/** The table row a stated grade is, within the rounding of a unit conversion. */
function gradeRow(table: readonly EraRow[], strength: number): EraRow | null {
  return table.find((row) => Math.abs(row.strength - strength) <= row.strength * 0.02) ?? null;
}

const round1 = (n: number): number => Math.round(n * 10) / 10;

/**
 * Concrete's allowable working stresses for a given f′c.
 *
 * CBPLR's own row where the grade is one of its rows, so the numbers are the
 * ones the workbook would type; otherwise the AASHTO Manual for Bridge
 * Evaluation's rule — 0.40 f′c at inventory and 0.60 f′c at operating
 * (MBE 6B.6.2.3) — which is what those rows are, near enough.
 */
export function concreteAllowable(
  fc: number | null | undefined,
): { inventory: number; operating: number; why: string } | null {
  if (fc == null || !(fc > 0)) return null;
  const row = gradeRow(DPWH_CBPLR.eras.concrete, fc);
  if (row !== null) {
    return {
      inventory: row.allowable.inventory,
      operating: row.allowable.operating,
      why: `The concrete allowables ${row.allowable.inventory} / ${row.allowable.operating} MPa are CBPLR’s own row for f′c ${row.strength} MPa.`,
    };
  }
  return {
    inventory: round1(0.4 * fc),
    operating: round1(0.6 * fc),
    why: `The concrete allowables ${round1(0.4 * fc)} / ${round1(0.6 * fc)} MPa are 0.40 f′c and 0.60 f′c (AASHTO MBE 6B.6.2.3) of f′c ${round1(fc)} MPa.`,
  };
}

/**
 * Reinforcement's allowable working stresses for a given fy.
 *
 * TABULATED BY GRADE, NOT A FRACTION OF fy: 20 / 28 ksi for Grade 40 and 24 / 36
 * ksi for Grade 60 (MBE Table 6B.6.2.3-1), and CBPLR's table carries the same
 * rows in MPa. The highest grade at or below fy is taken, so a Grade 75 bar gets
 * Grade 60's stresses rather than an extrapolation.
 */
export function rebarAllowable(
  fy: number | null | undefined,
): { inventory: number; operating: number; why: string } | null {
  if (fy == null || !(fy > 0)) return null;
  const rows = DPWH_CBPLR.eras.rebar.filter((row) => row.strength <= fy * 1.02);
  if (rows.length === 0) return null;
  const row = rows.reduce((a, b) => (b.strength > a.strength ? b : a));
  return {
    inventory: row.allowable.inventory,
    operating: row.allowable.operating,
    why: `The reinforcement allowables ${row.allowable.inventory} / ${row.allowable.operating} MPa are CBPLR’s ${row.label} row (fy ${row.strength} MPa; AASHTO MBE Table 6B.6.2.3-1) for fy ${round1(fy)} MPa.`,
  };
}

/**
 * The standard values this family asks for, given what is already known.
 *
 * `known` is whatever the form or the model already holds, as numbers; it is
 * read for the f′c and fy the allowables are keyed to, and a key already in it
 * is not offered again.
 */
export function standardDefaults(
  family: DefaultFamily,
  known: Readonly<Record<string, number | undefined>>,
): StandardValue[] {
  const out: StandardValue[] = [];
  const offer = (key: string, value: number, why: string): void => {
    if (known[key] !== undefined) return;
    out.push({ key, value, why });
  };

  /* THE ALLOWABLES follow from the grade, wherever the grade came from. PCDG
     asks for none: its serviceability limits are fractions of f′c written into
     the method. */
  if (family !== 'PCDG') {
    const concrete = concreteAllowable(known['concrete.strength']);
    if (concrete !== null) {
      offer('concrete.allowable.inventory', concrete.inventory, concrete.why);
      offer('concrete.allowable.operating', concrete.operating, concrete.why);
    }
    const rebar = rebarAllowable(known['rebar.strength']);
    if (rebar !== null) {
      offer('rebar.allowable.inventory', rebar.inventory, rebar.why);
      offer('rebar.allowable.operating', rebar.operating, rebar.why);
    }
  }

  /* THE DAMAGE INPUTS at zero, the undamaged as-built section. */
  const damage =
    'Section loss and crack depth are zero, the undamaged as-built section, because they are what an inspection finds. Replace them with the inspection’s findings before the rating is signed.';
  if (family === 'PCDG') offer('prestress.sectionLoss', 0, damage);
  else offer('sectionLoss', 0, damage);
  if (family !== 'RCFS') {
    offer('stirrupSectionLoss', 0, damage);
    offer('shearCrackDepth', 0, damage);
  }

  /* CBPLR's own method constants. */
  if (family === 'RCFS') offer('effectiveWidth', 1000, 'CBPLR’s one-metre rating strip (CCLFM!H29).');
  if (family === 'PCDG') offer('prestress.lossFraction', 0.03, 'The workbook’s own 0.03 (CCMS!H8). Read correction C19 before changing it.');

  /* THE LOADS every CBPLR workbook types, and the standard barrier. */
  offer('concreteUnitWeight', 24, '24 kN/m³, what every CBPLR workbook types — within a few per cent of AASHTO’s normal-weight concrete.');
  offer('asphaltUnitWeight', 22, '22 kN/m³, what every CBPLR workbook types for the asphalt overlay.');
  offer('wearingCourse', 50, 'A 50 mm asphalt overlay, the usual surfacing on a concrete bridge deck. Measure it on site.');
  offer(
    'barrierRail',
    6,
    'One 32-inch New Jersey concrete barrier, about 0.25 m² of section (roughly 405 lb/ft) at 24 kN/m³. Replace it with the barrier actually on the bridge.',
  );

  /* A T-beam's web fillets, which most cast-in-place tees are drawn without. */
  if (family === 'RCDG') {
    offer('filletWidth', 0, 'No web-to-slab fillet assumed. Enter it if the drawing shows fillets.');
    offer('filletHeight', 0, 'No web-to-slab fillet assumed. Enter it if the drawing shows fillets.');
  }

  return out;
}
